import React, { useState } from 'react'
import { Input, Button, Select, Icon, Modal } from 'antd'

const { Option } = Select

// ─── DialogueEditor ───────────────────────────────────────────────────────────
// Props:
//   dialogue    = { [characterKey]: "dialogue text", ... }
//   onChange    = (newDialogue) => void
//   disabled    = bool
//   formData    = { characterMap: { [characterKey]: "Display Name", ... } }
// ─────────────────────────────────────────────────────────────────────────────

const DialogueEditor = ({
  dialogue = {},
  onChange,
  disabled,
  formData = {}
}) => {
  let characterMap = formData?.characterMap || {}
  if (typeof characterMap === 'string') {
    try {
      characterMap = JSON.parse(characterMap)
    } catch (e) {
      characterMap = {}
    }
  }

  const [isOpen, setIsOpen] = useState(false)
  const [isAddingModalOpen, setIsAddingModalOpen] = useState(false)
  const [selectedCharKey, setSelectedCharKey] = useState(undefined)
  const [newDialogueText, setNewDialogueText] = useState('')

  // ── Read helpers ─────────────────────────────────────────────────────────────
  const entries = Object.entries(dialogue)
  const hasDialogue = entries.length > 0
  const allCharacters = Object.entries(characterMap)

  // ── Write helpers ─────────────────────────────────────────────────────────────

  function updateDialogue(characterKey, text) {
    onChange({ ...dialogue, [characterKey]: text })
  }

  function deleteDialogue(characterKey) {
    Modal.confirm({
      title: 'Delete Dialogue?',
      content:
        'Are you sure you want to remove this dialogue? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      centered: true,
      maskClosable: true,
      onOk() {
        const next = { ...dialogue }
        delete next[characterKey]
        onChange(next)
      }
    })
  }

  function addDialogue() {
    if (!selectedCharKey) return
    onChange({ ...dialogue, [selectedCharKey]: newDialogueText })
    closeModal()
  }

  function closeModal() {
    setSelectedCharKey(undefined)
    setNewDialogueText('')
    setIsAddingModalOpen(false)
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={styles.container}>
      {/* ── Header / toggle ── */}
      <div style={styles.header} onClick={() => setIsOpen(!isOpen)}>
        <div style={styles.headerLeft}>
          <Icon type="message" style={{ color: '#8c8c8c', fontSize: 14 }} />
          <span style={styles.headerLabel}>Shot Dialogue</span>
          {hasDialogue && !isOpen && (
            <span style={styles.badge}>{entries.length}</span>
          )}
        </div>
        <Icon
          type={isOpen ? 'up' : 'down'}
          style={styles.chevron(hasDialogue && !isOpen)}
        />
      </div>

      {/* ── Expanded body ── */}
      {isOpen && (
        <div style={styles.body}>
          {/* ── Empty state ── */}
          {!hasDialogue && (
            <div style={styles.emptyState}>
              <Icon type="info-circle" style={{ marginRight: 6 }} />
              No dialogue added yet.
            </div>
          )}

          {/* ── Existing dialogue entries ── */}
          {hasDialogue && (
            <div style={styles.list}>
              {entries.map(([key, text]) => {
                const displayName =
                  characterMap[key] || key.replace(/^__|__$/g, '')
                return (
                  <div key={key} style={styles.entry}>
                    <div style={styles.entryHeader}>
                      <span style={styles.charName}>
                        {displayName} ({key})
                      </span>
                      {!disabled && (
                        <Icon
                          type="delete"
                          onClick={() => deleteDialogue(key)}
                          style={styles.deleteIcon}
                          title="Delete Dialogue"
                        />
                      )}
                    </div>
                    <Input.TextArea
                      value={text}
                      disabled={disabled}
                      onChange={e => updateDialogue(key, e.target.value)}
                      autoSize={{ minRows: 1, maxRows: 6 }}
                      style={styles.textarea}
                    />
                  </div>
                )
              })}
            </div>
          )}

          {/* ── Add dialogue button ── */}
          {!disabled && (
            <Button
              type="dashed"
              icon="plus"
              size="small"
              block
              onClick={() => setIsAddingModalOpen(true)}
              style={styles.addButton}
            >
              Add Dialogue
            </Button>
          )}
        </div>
      )}

      {/* ── Add Dialogue Modal ── */}
      <Modal
        title="Add Character Dialogue"
        visible={isAddingModalOpen}
        onOk={addDialogue}
        onCancel={closeModal}
        okText="Add"
        cancelText="Cancel"
        okButtonProps={{
          disabled: !selectedCharKey || !newDialogueText?.trim()
        }}
        destroyOnClose
        centered
        width={420}
        bodyStyle={{ paddingTop: 16, paddingBottom: 16 }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <div
              style={{
                marginBottom: 6,
                fontSize: 13,
                fontWeight: 500,
                color: '#262626'
              }}
            >
              Select Character <span style={{ color: '#ff4d4f' }}>*</span>
            </div>
            <Select
              showSearch
              placeholder="Search and select character…"
              optionFilterProp="children"
              value={selectedCharKey}
              onChange={setSelectedCharKey}
              style={{ width: '100%' }}
            >
              {allCharacters.map(([key, name]) => (
                <Option key={key} value={key}>
                  {name} ({key})
                </Option>
              ))}
            </Select>
          </div>
          <div>
            <div
              style={{
                marginBottom: 6,
                fontSize: 13,
                fontWeight: 500,
                color: '#262626'
              }}
            >
              Dialogue Text <span style={{ color: '#ff4d4f' }}>*</span>
            </div>
            <Input.TextArea
              placeholder="Enter dialogue here…"
              value={newDialogueText}
              onChange={e => setNewDialogueText(e.target.value)}
              autoSize={{ minRows: 4, maxRows: 10 }}
              disabled={!selectedCharKey}
              style={{ fontSize: 13 }}
            />
          </div>
        </div>
      </Modal>
    </div>
  )
}

// ── Styles (inline so the component is self-contained) ────────────────────────
const styles = {
  container: {
    border: '1px solid #eaeaea',
    borderRadius: 6,
    overflow: 'hidden',
    marginTop: 12,
    backgroundColor: '#ffffff',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.02)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 14px',
    cursor: 'pointer',
    background: '#fafafa',
    userSelect: 'none',
    borderBottom: '1px solid transparent',
    transition: 'background 0.2s ease'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  headerLabel: {
    fontSize: 14,
    fontWeight: 500,
    color: '#262626'
  },
  badge: {
    background: '#1890ff',
    color: '#fff',
    borderRadius: 12,
    padding: '0 8px',
    fontSize: 12,
    fontWeight: 500,
    lineHeight: '20px'
  },
  chevron: active => ({
    fontSize: 12,
    color: active ? '#1890ff' : '#bfbfbf',
    transition: 'transform 0.3s ease'
  }),
  body: {
    padding: '14px',
    borderTop: '1px solid #eaeaea'
  },
  emptyState: {
    color: '#8c8c8c',
    fontSize: 13,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 0',
    background: '#fafafa',
    borderRadius: 4,
    border: '1px dashed #d9d9d9'
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginBottom: 12
  },
  entry: {
    background: '#fafafa',
    border: '1px solid #f0f0f0',
    borderRadius: 6,
    padding: '12px',
    transition: 'border-color 0.2s ease'
  },
  entryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8
  },
  charName: {
    fontSize: 13,
    fontWeight: 600,
    color: '#262626'
  },
  deleteIcon: {
    color: '#ff4d4f',
    cursor: 'pointer',
    fontSize: 14,
    padding: '4px',
    opacity: 0.8,
    transition: 'opacity 0.2s'
  },
  textarea: {
    fontSize: 13,
    color: '#434343',
    resize: 'none',
    boxShadow: 'none',
    border: '1px solid #d9d9d9',
    borderRadius: 4
  },
  addButton: {
    color: '#595959',
    borderColor: '#d9d9d9',
    background: '#fafafa',
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6
  }
}

export default DialogueEditor
